import {
  isCanonicalKanbanL2PropertyShape,
  KANBAN_L2_GROUP_MATCHER_NAMES,
} from '@streamlain/database-identity/database-view-defaults';

import type { DataSource } from '../../core/data-source/base.js';
import { getGroupByService } from '../../core/group-by/matcher.js';
import { t } from '../../core/logical/type-presets.js';

/**
 * A column qualifies as a Level-2 Kanban group source when:
 * - it has a registered GroupByConfig (so the rendering pipeline can
 *   resolve keys, labels, and DnD writes), AND
 * - it stores exactly one value per cell. Multi-valued columns
 *   (multi-select, multi-member, multi-relation) are rejected
 *   because they produce a Card in N rows — the 2-D swimlane grid
 *   requires cell-uniqueness for deterministic drop targets.
 */
export const isL2Groupable = (
  dataSource: DataSource,
  columnId: string
): boolean => {
  const propertyType = dataSource.propertyTypeGet(columnId);
  if (!propertyType) return false;
  const dataType = dataSource.propertyDataTypeGet(columnId);
  if (!dataType) return false;
  const data = dataSource.propertyDataGet(columnId);
  const cardinality = t.array.is(dataType)
    ? (data as { cardinality?: 'one' | 'many' }).cardinality
    : undefined;
  if (!isCanonicalKanbanL2PropertyShape({ type: propertyType, cardinality }))
    return false;

  const svc = getGroupByService(dataSource);
  const match = svc?.matcher.match(dataType);
  if (!match) return false;
  return KANBAN_L2_GROUP_MATCHER_NAMES.has(match.name);
};
